import {
  unchunkApplogsBlock
} from "./chunk-EYY3KCRO.js";
import {
  removeDuplicateAppLogs
} from "./chunk-236RMLMX.js";
import {
  areCidsEqual
} from "./chunk-KPMINROC.js";

// src/retrieve/update-thread.ts
import * as dagJson from "@ipld/dag-json";
import { Logger } from "besonders-logger";
import { CID } from "multiformats/cid";
var { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO);
function withBlockCache(source, store) {
  return {
    get: (cid) => store.get(cid),
    async *getDag(cid) {
      for await (const block of source.getDag(cid)) {
        await store.put(block.cid, block.bytes);
        yield block;
      }
    }
  };
}
function createMemoryBlockStore() {
  const blocks = /* @__PURE__ */ new Map();
  return {
    get(cid) {
      return blocks.get(cid.toV1().toString());
    },
    put(cid, bytes) {
      blocks.set(cid.toV1().toString(), bytes);
    }
  };
}
async function getDecodedBlock(blockStore, cid) {
  const bytes = blockStore.get(cid);
  if (!bytes) return null;
  return dagJson.decode(bytes);
}
async function updateThreadFromSnapshot(thread, cid, retriever, options) {
  const { excludeSnapshotCID, stopAtCounter, maxDepth = 100 } = options ?? {};
  DEBUG("[updateThreadFromSnapshot] starting from", cid.toString(), {
    excludeSnapshotCID: excludeSnapshotCID?.toString(),
    stopAtCounter,
    maxDepth
  });
  const blockStore = createMemoryBlockStore();
  const visited = /* @__PURE__ */ new Set();
  let currentCID = cid;
  let snapshotCount = 0;
  const allApplogs = [];
  let minCounter = Infinity;
  let maxCounter = -Infinity;
  let lastCounter;
  while (currentCID && snapshotCount < maxDepth) {
    const cidStr = currentCID.toString();
    if (visited.has(cidStr)) {
      throw ERROR("[updateThreadFromSnapshot] snapshot chain has a loop", {
        currentCID: cidStr,
        visited: [...visited]
      });
    }
    visited.add(cidStr);
    if (excludeSnapshotCID && areCidsEqual(currentCID, excludeSnapshotCID)) {
      DEBUG("[updateThreadFromSnapshot] reached excludeSnapshotCID, stopping", excludeSnapshotCID.toString());
      break;
    }
    DEBUG("[updateThreadFromSnapshot] fetching root block", cidStr);
    const rootBytes = await retriever.get(currentCID);
    blockStore.put(currentCID, rootBytes);
    const root = dagJson.decode(rootBytes);
    if (typeof root.prevCounter === "number") {
      minCounter = Math.min(minCounter, root.prevCounter);
      maxCounter = Math.max(maxCounter, root.prevCounter);
      if (lastCounter !== void 0 && root.prevCounter !== lastCounter - 1) {
        WARN("[updateThreadFromSnapshot] counter gap detected", {
          expected: lastCounter - 1,
          got: root.prevCounter
        });
      }
      lastCounter = root.prevCounter;
    }
    if (stopAtCounter !== void 0 && typeof root.prevCounter === "number" && root.prevCounter <= stopAtCounter) {
      DEBUG("[updateThreadFromSnapshot] reached stopAtCounter", { stopAtCounter, prevCounter: root.prevCounter });
      break;
    }
    DEBUG("[updateThreadFromSnapshot] fetching applogs", root.applogs.toString());
    for await (const { cid: blockCid, bytes } of retriever.getDag(root.applogs)) {
      blockStore.put(blockCid, bytes);
    }
    DEBUG("[updateThreadFromSnapshot] fetching info", root.info.toString());
    for await (const { cid: blockCid, bytes } of retriever.getDag(root.info)) {
      blockStore.put(blockCid, bytes);
    }
    const applogsBlock = await getDecodedBlock(blockStore, root.applogs);
    if (!applogsBlock) {
      throw ERROR("[updateThreadFromSnapshot] applogs block not found", { cid: root.applogs.toString() });
    }
    const applogCIDs = await unchunkApplogsBlock(applogsBlock, {
      get: async (cid2) => blockStore.get(cid2)
    });
    for (const applogCID of applogCIDs) {
      const applog = await getDecodedBlock(blockStore, applogCID);
      if (!applog) {
        WARN("[updateThreadFromSnapshot] applog not found:", applogCID.toString());
        continue;
      }
      const pvCid = CID.asCID(applog.pv);
      if (pvCid) {
        applog.pv = pvCid.toV1().toString();
      }
      allApplogs.push({
        ...applog,
        cid: applogCID.toV1().toString()
      });
    }
    snapshotCount++;
    currentCID = root.prev;
  }
  DEBUG("[updateThreadFromSnapshot] fetched", {
    snapshotCount,
    applogCount: allApplogs.length,
    rootCID: cid.toString()
  });
  const deduplicated = removeDuplicateAppLogs(allApplogs, "cleanup");
  const inserted = thread.insertMissing(deduplicated, false);
  DEBUG("[updateThreadFromSnapshot] inserted", {
    insertedCount: inserted.length,
    duplicateCount: deduplicated.length - inserted.length
  });
  return {
    cid,
    applogs: deduplicated,
    insertedCount: inserted.length,
    snapshotCount,
    counterRange: minCounter !== Infinity ? { minCounter, maxCounter } : void 0
  };
}

export {
  withBlockCache,
  updateThreadFromSnapshot
};
//# sourceMappingURL=chunk-44VRVDWS.js.map